import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Printer, ArrowLeft, FileText, Loader2 } from "lucide-react";
import { RT, STATUS_COLORS } from "@/components/rmsreport/ui";
import { AssistantWidget } from "@/components/AssistantWidget";
import SectionIncident from "@/components/rmsreport/SectionIncident";
import SectionEntities from "@/components/rmsreport/SectionEntities";
import SectionEms from "@/components/rmsreport/SectionEms";
import SectionNarrative from "@/components/rmsreport/SectionNarrative";
import SectionCad from "@/components/rmsreport/SectionCad";
import SectionEvidence from "@/components/rmsreport/SectionEvidence";
import SectionAttachments from "@/components/rmsreport/SectionAttachments";
import SectionSupplements from "@/components/rmsreport/SectionSupplements";
import SectionApprovals from "@/components/rmsreport/SectionApprovals";
import SectionAudit from "@/components/rmsreport/SectionAudit";

const NAV = (r) => [
  ["incident", "INCIDENT"], ["narrative", "NARRATIVE"],
  ["persons", `PERSONS (${r.persons.length})`], ["vehicles", `VEHICLES (${r.vehicles.length})`],
  ["units", `UNITS (${r.units.length})`], ["cad", "CAD EVENTS"],
  ["evidence", `EVIDENCE (${r.evidence.length})`], ["property", `PROPERTY (${r.property.length})`],
  ["ems", "EMS / FIRE"], ["attachments", `ATTACHMENTS (${r.attachments.length})`],
  ["supplements", `SUPPLEMENTS (${r.supplements.length})`], ["approvals", "APPROVALS"], ["audit", "AUDIT HISTORY"],
];

export default function RmsReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rep, setRep] = useState(null);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("incident");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cadNew, setCadNew] = useState(0);
  const [cadDismissed, setCadDismissed] = useState(false);
  const load = useCallback(() => {
    client.get(`/rms-reports/${id}`).then((r) => setRep(r.data)).catch(() => toast.error("Report not found"));
  }, [id]);
  useEffect(() => { load(); client.get("/rms-reports/meta").then((r) => setMeta(r.data)); }, [load]);
  useEffect(() => {
    if (rep?.id) client.get(`/rms-reports/${rep.id}/cad-check`).then((r) => setCadNew(r.data.new_events)).catch(() => {});
  }, [rep?.id, rep?.status]);
  if (!rep || !meta) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: RT.page }}>
      <Loader2 className="animate-spin" style={{ color: RT.head }} />
    </div>
  );
  const isSup = ["supervisor", "watch_commander", "records_supervisor", "admin"].includes(user?.role);
  const canEdit = ["DRAFT", "RETURNED"].includes(rep.status) && (rep.created_by_user_id === user?.id || isSup);
  const pdf = async () => {
    setPdfBusy(true);
    try {
      const r = await client.get(`/rms-reports/${rep.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${rep.case_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF export failed"); }
    setPdfBusy(false);
  };
  const common = { rep, meta, canEdit, onChanged: load, user };
  return (
    <div className="min-h-screen flex flex-col" style={{ background: RT.page }} data-testid="rms-report-page">
      {/* FIXED HEADER */}
      <div className="px-4 py-2.5 flex items-center gap-4" style={{ background: RT.navy }}>
        <button data-testid="rr-back" onClick={() => nav(-1)} className="text-white/70 hover:text-white"><ArrowLeft size={17} /></button>
        <div>
          <div className="font-head font-bold text-[13px] text-white tracking-wide">MATCOM OFFICE OF PUBLIC SAFETY</div>
          <div className="font-mono text-[9px] text-white/60 tracking-widest">RECORDS MANAGEMENT SYSTEM</div>
        </div>
        <div className="h-8 w-px bg-white/20" />
        <div className="flex items-center gap-3">
          <span className="font-head font-bold text-sm text-white">INCIDENT REPORT</span>
          <span className="font-mono text-[12px] text-[#7dd3fc]" data-testid="rr-case-number">{rep.case_number}</span>
          <button data-testid="rr-cad-link" onClick={() => nav(`/incident/${rep.incident_id}`)}
            className="font-mono text-[11px] text-white/70 underline hover:text-white">CASE/CFS: {rep.cad_number}</button>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span data-testid="rr-status" className="px-2.5 py-1 font-head font-bold text-[11px] text-white rounded-sm"
            style={{ background: STATUS_COLORS[rep.status] || "#334155" }}>STATUS: {rep.status}</span>
          <button data-testid="rr-pdf" onClick={pdf} disabled={pdfBusy}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-head font-bold text-white rounded-sm disabled:opacity-50"
            style={{ background: RT.head }}>
            {pdfBusy ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />} PRINT / PDF
          </button>
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        {/* NEW CAD INFO BANNER handled above content */}
        {/* LEFT NAVIGATION */}
        <div className="w-44 shrink-0 py-2 overflow-y-auto" style={{ background: RT.nav }}>
          {NAV(rep).map(([key, label]) => (
            <button key={key} data-testid={`rr-nav-${key}`} onClick={() => setTab(key)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left font-head font-bold text-[10.5px] tracking-wide border-l-4 transition-colors"
              style={tab === key ? { background: RT.navActive, color: "#fff", borderColor: "#7dd3fc" }
                : { color: "#9fb6c9", borderColor: "transparent" }}>
              <FileText size={12} className="shrink-0 opacity-60" /> {label}
            </button>
          ))}
        </div>
        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-3">
          {cadNew > 0 && !cadDismissed && ["DRAFT", "RETURNED"].includes(rep.status) && (
            <div className="mb-3 border-l-4 border-[#b45309] bg-[#fdf6ec] px-3 py-2 flex items-center gap-3" data-testid="cad-update-banner">
              <span className="font-mono text-[11px] font-bold text-[#b45309]">
                NEW CAD/MDT INFORMATION AVAILABLE — {cadNew} event(s) were added to the CAD call after this draft was generated.
              </span>
              <button data-testid="cad-incorporate" onClick={async () => {
                if (!rep.narrative_auto_generated && !window.confirm("Incorporating regenerates the draft from current CAD data and replaces officer-typed narrative text. Continue?")) return;
                try { await client.post(`/rms-reports/${rep.id}/regenerate`); toast.success("Draft updated from CAD"); setCadNew(0); load(); }
                catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
              }} className="ml-auto px-2 py-1 text-[10px] font-head font-bold text-white rounded-sm shrink-0" style={{ background: "#b45309" }}>INCORPORATE INTO DRAFT</button>
              <button data-testid="cad-dismiss" onClick={() => setCadDismissed(true)}
                className="px-2 py-1 text-[10px] font-head font-bold rounded-sm border shrink-0" style={{ color: "#b45309", borderColor: "#b45309" }}>DISMISS</button>
            </div>
          )}
          {tab === "incident" && <SectionIncident {...common} />}
          {tab === "narrative" && <SectionNarrative {...common} />}
          {tab === "persons" && <SectionEntities {...common} only="persons" />}
          {tab === "vehicles" && <SectionEntities {...common} only="vehicles" />}
          {tab === "units" && <SectionEntities {...common} only="units" />}
          {tab === "cad" && <SectionCad rep={rep} />}
          {tab === "evidence" && <SectionEvidence {...common} only="evidence" />}
          {tab === "property" && <SectionEvidence {...common} only="property" />}
          {tab === "ems" && <SectionEms {...common} />}
          {tab === "attachments" && <SectionAttachments {...common} />}
          {tab === "supplements" && <SectionSupplements {...common} />}
          {tab === "approvals" && <SectionApprovals {...common} />}
          {tab === "audit" && <SectionAudit rep={rep} />}
        </div>
      </div>
      <AssistantWidget />
    </div>
  );
}
